import { createStatusOptions } from '@/config/statusOptions';

// One vocabulary per backend enum — a status reads the same wherever its entity is shown.

// `contract_status`.
export const CONTRACT_STATUS = Object.freeze({ active: 'ACTIVE', closed: 'CLOSED' });

export const CONTRACT_STATUS_LABEL_KEYS = Object.freeze({
  [CONTRACT_STATUS.active]: 'STATUS_CONTRACT_ACTIVE',
  [CONTRACT_STATUS.closed]: 'STATUS_CONTRACT_CLOSED',
});

export const {
  statusLabel: contractStatusLabel,
  buildStatusVariants: buildContractStatusVariants,
  buildStatusFilterOptions: buildContractStatusFilterOptions,
} = createStatusOptions(CONTRACT_STATUS_LABEL_KEYS);

// `ReceivedStatus`. TO_RECEIVED is a server-side typo for TO_RECEIVE; the label reads right.
export const SHIPMENT_STATUS_LABEL_KEYS = Object.freeze({
  TO_RECEIVED: 'STATUS_SHIPMENT_TO_RECEIVE',
  PARTIALLY_RECEIVED: 'STATUS_SHIPMENT_PARTIALLY_RECEIVED',
  RECEIVED: 'STATUS_SHIPMENT_RECEIVED',
  FORWARD: 'STATUS_SHIPMENT_FORWARD',
});

// `ShipmentStatusFilter` is narrower: a FORWARD shipment's status column is NULL.
export const SHIPMENT_STATUS_FILTER_LABEL_KEYS = Object.freeze({
  TO_RECEIVED: SHIPMENT_STATUS_LABEL_KEYS.TO_RECEIVED,
  PARTIALLY_RECEIVED: SHIPMENT_STATUS_LABEL_KEYS.PARTIALLY_RECEIVED,
  RECEIVED: SHIPMENT_STATUS_LABEL_KEYS.RECEIVED,
});

export const {
  statusLabel: shipmentStatusLabel,
  buildStatusVariants: buildShipmentStatusVariants,
  buildStatusFilterOptions: buildShipmentStatusFilterOptions,
} = createStatusOptions(SHIPMENT_STATUS_LABEL_KEYS);

// `inbound_status` — a declaration's own status, and a linked batch's.
export const DECLARATION_STATUS_LABEL_KEYS = Object.freeze({
  DRAFT: 'STATUS_DECLARATION_DRAFT',
  PROCESSING: 'STATUS_DECLARATION_PROCESSING',
  COMPLETED: 'STATUS_DECLARATION_COMPLETED',
  PENDING: 'STATUS_DECLARATION_PENDING',
  CONSUMED: 'STATUS_DECLARATION_CONSUMED',
  REVOKED: 'STATUS_DECLARATION_REVOKED',
  REJECTED: 'STATUS_DECLARATION_REJECTED',
});

// The GraphQL `DeclarationStatus` enum — it omits the DB's REVOKED, which stays labelled.
export const DECLARATION_FILTER_STATUSES = Object.freeze([
  'DRAFT',
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'CONSUMED',
  'REJECTED',
]);

// The statuses that carry a received quantity — iscc-backend's RECEIVED_BATCH_STATUSES.
export const RECEIVED_DECLARATION_STATUSES = Object.freeze(['COMPLETED', 'CONSUMED']);

export const {
  statusLabel: declarationStatusLabel,
  buildStatusVariants: buildDeclarationStatusVariants,
  buildStatusFilterOptions: buildDeclarationStatusFilterOptions,
} = createStatusOptions(DECLARATION_STATUS_LABEL_KEYS);

// `declaration_verification_status`.
export const VERIFICATION_STATUS_LABEL_KEYS = Object.freeze({
  VERIFIED: 'STATUS_VERIFICATION_VERIFIED',
  MANUALLY_VERIFIED: 'STATUS_VERIFICATION_MANUALLY_VERIFIED',
  PENDING: 'STATUS_VERIFICATION_PENDING',
  REJECTED: 'STATUS_VERIFICATION_REJECTED',
  NA: 'STATUS_VERIFICATION_NA',
});

export const {
  statusLabel: verificationStatusLabel,
  buildStatusVariants: buildVerificationStatusVariants,
} = createStatusOptions(VERIFICATION_STATUS_LABEL_KEYS);

// `pending_order.fulfillment_status`; shared with the orders slice's Linked shipment tab.
export const OUTGOING_SHIPMENT_STATUS = Object.freeze({
  pending: 'PENDING',
  partiallyAllocated: 'PARTIALLY_ALLOCATED',
  allocated: 'ALLOCATED',
  cancelled: 'CANCELLED',
});

export const OUTGOING_SHIPMENT_STATUS_LABEL_KEYS = Object.freeze({
  [OUTGOING_SHIPMENT_STATUS.pending]: 'OUTGOING_SHIPMENTS_STATUS_PENDING',
  [OUTGOING_SHIPMENT_STATUS.partiallyAllocated]: 'OUTGOING_SHIPMENTS_STATUS_PARTIALLY_ALLOCATED',
  [OUTGOING_SHIPMENT_STATUS.allocated]: 'OUTGOING_SHIPMENTS_STATUS_ALLOCATED',
  [OUTGOING_SHIPMENT_STATUS.cancelled]: 'OUTGOING_SHIPMENTS_STATUS_CANCELLED',
});

export const {
  statusLabel: outgoingShipmentStatusLabel,
  buildStatusVariants: buildOutgoingShipmentStatusVariants,
} = createStatusOptions(OUTGOING_SHIPMENT_STATUS_LABEL_KEYS);

// `approvalStatus` — English text, not an enum; shared with the orders slice's Allocation tab.
export const ALLOCATION_STATUS = Object.freeze({
  pendingDeclaration: 'Pending declaration',
  pendingShipment: 'Pending shipment',
  declarationGenerated: 'Declaration generated',
  declarationUpdated: 'Declaration updated',
  declarationRejected: 'Declaration rejected',
  creditsRetired: 'Credits retired',
  revoked: 'Revoked',
});

export const ALLOCATION_STATUS_LABEL_KEYS = Object.freeze({
  [ALLOCATION_STATUS.pendingDeclaration]: 'ALLOCATIONS_STATUS_PENDING_DECLARATION',
  [ALLOCATION_STATUS.pendingShipment]: 'ALLOCATIONS_STATUS_PENDING_SHIPMENT',
  [ALLOCATION_STATUS.declarationGenerated]: 'ALLOCATIONS_STATUS_DECLARATION_GENERATED',
  [ALLOCATION_STATUS.declarationUpdated]: 'ALLOCATIONS_STATUS_DECLARATION_UPDATED',
  [ALLOCATION_STATUS.declarationRejected]: 'ALLOCATIONS_STATUS_DECLARATION_REJECTED',
  [ALLOCATION_STATUS.creditsRetired]: 'ALLOCATIONS_STATUS_CREDITS_RETIRED',
  [ALLOCATION_STATUS.revoked]: 'ALLOCATIONS_STATUS_REVOKED',
});

export const {
  statusLabel: allocationStatusLabel,
  buildStatusVariants: buildAllocationStatusVariants,
} = createStatusOptions(ALLOCATION_STATUS_LABEL_KEYS);

// The strings computePendingOrderDisplayStatus returns — codes, not copy; the labels are below.
export const ORDER_DISPLAY_STATUS = Object.freeze({
  active: 'Active',
  closed: 'Closed',
  cancelled: 'Cancelled',
});

export const ORDER_DISPLAY_STATUS_LABEL_KEYS = Object.freeze({
  [ORDER_DISPLAY_STATUS.active]: 'ORDERS_DETAIL_STATUS_ACTIVE',
  [ORDER_DISPLAY_STATUS.closed]: 'ORDERS_DETAIL_STATUS_CLOSED',
  [ORDER_DISPLAY_STATUS.cancelled]: 'ORDERS_DETAIL_STATUS_CANCELLED',
});

export const {
  statusLabel: orderDisplayStatusLabel,
  buildStatusVariants: buildOrderDisplayStatusVariants,
} = createStatusOptions(ORDER_DISPLAY_STATUS_LABEL_KEYS);

// `transaction_status` — the RAW enum, unlike ALLOCATION_STATUS above; NA is left unmapped so it dashes.
export const TRANSACTION_STATUS_LABEL_KEYS = Object.freeze({
  PENDING_DECLARATION: 'ALLOCATIONS_STATUS_PENDING_DECLARATION',
  PENDING_SHIPMENT: 'ALLOCATIONS_STATUS_PENDING_SHIPMENT',
  AWAITING_APPROVAL: 'STATUS_TRANSACTION_AWAITING_APPROVAL',
  REJECTED: 'STATUS_TRANSACTION_REJECTED',
  TRANSFERRED: 'STATUS_TRANSACTION_TRANSFERRED',
  DECLARATION_GENERATED: 'ALLOCATIONS_STATUS_DECLARATION_GENERATED',
  DECLARATION_UPDATED: 'ALLOCATIONS_STATUS_DECLARATION_UPDATED',
  CREDITS_RETIRED: 'ALLOCATIONS_STATUS_CREDITS_RETIRED',
  DISPATCHED: 'STATUS_TRANSACTION_DISPATCHED',
  REVOKED: 'ALLOCATIONS_STATUS_REVOKED',
});

export const {
  statusLabel: transactionStatusLabel,
  buildStatusVariants: buildTransactionStatusVariants,
} = createStatusOptions(TRANSACTION_STATUS_LABEL_KEYS);
export const CERTIFICATE_TYPE = Object.freeze({
  isccEu: 'ISCC_EU',
  isccPlus: 'ISCC_PLUS',
  isccCorsia: 'ISCC_CORSIA',
});

export const CERTIFICATE_TYPE_LABEL_KEYS = Object.freeze({
  [CERTIFICATE_TYPE.isccEu]: 'CERTIFICATE_TYPE_ISCC_EU',
  [CERTIFICATE_TYPE.isccPlus]: 'CERTIFICATE_TYPE_ISCC_PLUS',
  [CERTIFICATE_TYPE.isccCorsia]: 'CERTIFICATE_TYPE_ISCC_CORSIA',
});

export const {
  statusLabel: certificateTypeLabel,
  buildStatusVariants: buildCertificateTypeVariants,
  buildStatusFilterOptions: buildCertificateTypeFilterOptions,
} = createStatusOptions(CERTIFICATE_TYPE_LABEL_KEYS);

export const MATERIAL_TYPE_LABEL_KEYS = Object.freeze({
  CONVENTIONAL: 'MATERIAL_TYPE_CONVENTIONAL',
  CERTIFIED: 'MATERIAL_TYPE_CERTIFIED',
});

export const {
  statusLabel: materialTypeLabel,
  buildStatusFilterOptions: buildMaterialTypeOptions,
} = createStatusOptions(MATERIAL_TYPE_LABEL_KEYS);

export const MATERIAL_ROLE_LABEL_KEYS = Object.freeze({
  INPUT: 'MATERIAL_ROLE_INPUT',
  OUTPUT: 'MATERIAL_ROLE_OUTPUT',
});

export const { statusLabel: materialRoleLabel } = createStatusOptions(MATERIAL_ROLE_LABEL_KEYS);

export const MATERIAL_DECLARATION_SETTINGS_LABEL_KEYS = Object.freeze({
  AVAILABLE: 'STATUS_DECLARATION_SETTINGS_AVAILABLE',
  NOT_AVAILABLE: 'STATUS_DECLARATION_SETTINGS_NOT_AVAILABLE',
});

export const { statusLabel: materialDeclarationSettingsLabel } = createStatusOptions(
  MATERIAL_DECLARATION_SETTINGS_LABEL_KEYS,
);

export const MULTIPLE_SITE_TYPES = 'MULTIPLE_SITE_TYPES';

export const SITE_TYPE_LABEL_KEYS = Object.freeze({
  TRADER: 'SITE_TYPE_TRADER',
  TRADER_WITH_STORAGE: 'SITE_TYPE_TRADER_WITH_STORAGE',
  CONVERTER: 'SITE_TYPE_CONVERTER',
  PROCESSING_UNIT: 'SITE_TYPE_PROCESSING_UNIT',
  WAREHOUSE: 'SITE_TYPE_WAREHOUSE',
  POINT_OF_ORIGIN: 'SITE_TYPE_POINT_OF_ORIGIN',
  BIO_DIESEL_PLANT: 'SITE_TYPE_BIO_DIESEL_PLANT',
});

export const { statusLabel: siteTypeLabel, buildStatusFilterOptions: buildSiteTypeFilterOptions } =
  createStatusOptions(SITE_TYPE_LABEL_KEYS);

export const CERTIFICATION_STATUS = Object.freeze({
  certified: 'CERTIFIED',
  notCertified: 'NOT_CERTIFIED',
});

export const CERTIFICATION_STATUS_LABEL_KEYS = Object.freeze({
  [CERTIFICATION_STATUS.certified]: 'STATUS_CERTIFICATION_CERTIFIED',
  [CERTIFICATION_STATUS.notCertified]: 'STATUS_CERTIFICATION_NOT_CERTIFIED',
});

export const {
  statusLabel: certificationStatusLabel,
  buildStatusFilterOptions: buildCertificationStatusOptions,
} = createStatusOptions(CERTIFICATION_STATUS_LABEL_KEYS);

// A customer site's `verification_status` — its own enum, not `declaration_verification_status`.
export const CUSTOMER_SITE_CERT_STATUS = Object.freeze({
  verified: 'VERIFIED',
  rejected: 'REJECTED',
  notAvailable: 'NA',
});

export const CUSTOMER_SITE_CERT_STATUS_LABEL_KEYS = Object.freeze({
  [CUSTOMER_SITE_CERT_STATUS.verified]: 'STATUS_CUSTOMER_SITE_CERT_VERIFIED',
  [CUSTOMER_SITE_CERT_STATUS.rejected]: 'STATUS_CUSTOMER_SITE_CERT_REJECTED',
  [CUSTOMER_SITE_CERT_STATUS.notAvailable]: 'STATUS_CUSTOMER_SITE_CERT_NA',
});

export const {
  statusLabel: customerSiteCertStatusLabel,
  buildStatusVariants: buildCustomerSiteCertStatusVariants,
} = createStatusOptions(CUSTOMER_SITE_CERT_STATUS_LABEL_KEYS);

export const MB_PERIOD_STATUS = Object.freeze({
  scheduled: 'SCHEDULED',
  open: 'OPEN',
  closed: 'CLOSED',
});

export const MB_PERIOD_STATUS_LABEL_KEYS = Object.freeze({
  [MB_PERIOD_STATUS.scheduled]: 'STATUS_MB_PERIOD_SCHEDULED',
  [MB_PERIOD_STATUS.open]: 'STATUS_MB_PERIOD_OPEN',
  [MB_PERIOD_STATUS.closed]: 'STATUS_MB_PERIOD_CLOSED',
});

export const { statusLabel: mbPeriodStatusLabel } = createStatusOptions(
  MB_PERIOD_STATUS_LABEL_KEYS,
);

// Monitor ▸ Transactions "Type" — a TransactionLedgerType, derived server-side.
export const TRANSACTION_LEDGER_TYPE_LABEL_KEYS = Object.freeze({
  INCOMING_DECLARATION: 'TRANSACTIONS_TYPE_INCOMING_DECLARATION',
  SURPLUS: 'TRANSACTIONS_TYPE_SURPLUS',
  WRITE_OFF: 'TRANSACTIONS_TYPE_WRITE_OFF',
  OUTGOING_DECLARATION: 'TRANSACTIONS_TYPE_OUTGOING_DECLARATION',
  REVERTED: 'TRANSACTIONS_TYPE_REVERTED',
});

export const {
  statusLabel: transactionLedgerTypeLabel,
  buildStatusVariants: buildTransactionTypeVariants,
  buildStatusFilterOptions: buildTransactionTypeFilterOptions,
} = createStatusOptions(TRANSACTION_LEDGER_TYPE_LABEL_KEYS);

// Inventory ▸ Summary ▸ Physical reconciliation — a sub-period's outcome; null reads as not applicable.
export const RECONCILIATION_STATUS_LABEL_KEYS = Object.freeze({
  Gain: 'RECONCILIATION_STATUS_GAIN',
  Loss: 'RECONCILIATION_STATUS_LOSS',
  Reconciled: 'RECONCILIATION_STATUS_RECONCILED',
});

export const { statusLabel: reconciliationStatusLabel } = createStatusOptions(
  RECONCILIATION_STATUS_LABEL_KEYS,
);

// Inventory ▸ Summary ▸ Material movement — iscc-backend's MOVEMENT_TYPE, five inbound then four outbound.
export const MOVEMENT_STATUS_LABEL_KEYS = Object.freeze({
  FORWARD_INBOUND: 'MOVEMENT_STATUS_FORWARD_INBOUND',
  RECEIVED: 'MOVEMENT_STATUS_RECEIVED',
  TO_RECEIVE: 'MOVEMENT_STATUS_TO_RECEIVE',
  SURPLUS: 'MOVEMENT_STATUS_SURPLUS',
  WRITE_OFF: 'MOVEMENT_STATUS_WRITE_OFF',
  FORWARD_OUTBOUND: 'MOVEMENT_STATUS_FORWARD_OUTBOUND',
  DISPATCHED: 'MOVEMENT_STATUS_DISPATCHED',
  ALLOCATED: 'MOVEMENT_STATUS_ALLOCATED',
  DECLARATION_GENERATED: 'MOVEMENT_STATUS_DECLARATION_GENERATED',
});

export const { statusLabel: movementStatusLabel } = createStatusOptions(MOVEMENT_STATUS_LABEL_KEYS);
